import config from '../../config';
import { apiFetch, authenticatedIdentity } from './apiFetch';
import { createRequestCache } from '../Page/HomePage/floorDeckRequestCache';

const cache = createRequestCache(30000);
let identity;

function session() {
    const current = authenticatedIdentity();
    if (current !== identity) {
        cache.clear();
        identity = current;
    }

    return current;
}

function path(kind, assetId, collection = 'checklists') {
    if (!['machines', 'testers', 'jig-fixtures'].includes(kind))
        throw new Error('Unsupported asset kind.');

    return `/machining/maintenance/${kind}/${collection}${assetId === undefined ? '' : '/' + encodeURIComponent(assetId)}`;
}

async function request(url, expectedIdentity, method = 'GET', payload) {
    const response = await apiFetch(config.apiUrl + url, {
        expectedIdentity,
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
    const data = await response.json();
    if (expectedIdentity !== authenticatedIdentity()) throw new Error('Account changed.');
    if (!response.ok) {
        if (response.status === 401 || response.status === 403) cache.clear();
        throw Object.assign(new Error(data.error || 'Unable to load maintenance.'), {
            status: response.status,
            fields: data.fields || {},
            latest: data.latest,
        });
    }

    return data;
}

function read(url, force = false) {
    const current = session();
    const key = JSON.stringify([current, url]);
    const loader = () => request(url, current);

    return force
        ? { data: cache.peek(key), revalidate: cache.get(key, loader, true) }
        : cache.getStaleWhileRevalidate(key, loader);
}

export function getChecklist(kind, assetId) {
    return read(path(kind, assetId)).revalidate;
}

export async function saveChecklist(kind, assetId, payload) {
    const current = session();
    cache.clear();
    try {
        return await request(path(kind, assetId), current, 'PUT', payload);
    } finally {
        cache.clear();
    }
}

async function mutateRecord(kind, id, operation, method, payload) {
    const current = session();
    cache.clear();
    try {
        return await request(path(kind, id, 'records') + '/' + operation, current, method, payload);
    } finally {
        cache.clear();
    }
}

async function mutateRequest(kind, id, method, payload, suffix = '') {
    const current = session();
    cache.clear();
    try {
        return await request(path(kind, id, 'requests') + suffix, current, method, payload);
    } finally {
        cache.clear();
    }
}

export const machiningMaintenanceService = {
    listSummary(kind, query = {}, force = false) {
        const parameters = new URLSearchParams(
            Object.entries(query)
                .filter(([, value]) => value !== '' && value != null)
                .sort(([a], [b]) => a.localeCompare(b))
        );
        return read(path(kind, undefined, 'requests') + '?' + parameters.toString(), force);
    },
    listMetrics(kind, query = {}, force = false) {
        const parameters = new URLSearchParams(
            Object.entries(query)
                .filter(([key, value]) => value !== '' && value != null && !['page', 'pageSize'].includes(key))
                .sort(([a], [b]) => a.localeCompare(b))
        );
        return read(path(kind, undefined, 'metrics') + '?' + parameters.toString(), force);
    },
    getRequest: (kind, id) => read(path(kind, id, 'requests'), true).revalidate,
    createRequest: (kind, payload) => mutateRequest(kind, undefined, 'POST', payload),
    saveRequest: (kind, id, payload) => mutateRequest(kind, id, 'PATCH', payload),
    submitRequest: (kind, id, revision) => mutateRequest(kind, id, 'POST', { revision }, '/submit'),
    async getImage(kind, id, signal) {
        const current = session();
        const response = await apiFetch(config.apiUrl + path(kind, id, 'attachments'), {
            expectedIdentity: current,
            signal,
        });
        if (!response.ok)
            throw Object.assign(new Error('Unable to load private photo.'), {
                status: response.status,
            });

        const blob = await response.blob();
        if (current !== authenticatedIdentity()) throw new Error('Account changed.');

        return blob;
    },
    async uploadMaintenanceImage(
        kind,
        { parentType, parentId, taskSeq, section, revision, file },
        signal
    ) {
        const current = session();
        const body = new FormData();
        body.append('parentType', parentType);
        body.append('parentId', parentId);
        if (taskSeq != null) body.append('taskSeq', taskSeq);
        if (section != null) body.append('section', section);
        if (revision != null) body.append('revision', revision);
        body.append('file', file);
        const response = await apiFetch(config.apiUrl + path(kind, undefined, 'attachments'), {
            expectedIdentity: current,
            method: 'POST',
            body,
            signal,
        });
        const data = await response.json();
        if (current !== authenticatedIdentity()) throw new Error('Account changed.');
        if (!response.ok)
            throw Object.assign(new Error(data.error || 'Photo upload failed.'), {
                status: response.status,
                fields: data.fields,
                latest: data.latest,
            });

        return data;
    },
    removeImage: (kind, id, revision) =>
        request(path(kind, id, 'attachments'), session(), 'DELETE', { revision }),
    listImages(kind, { parentType, parentId, taskSeq, section }) {
        const parameters = new URLSearchParams(
            Object.entries({ parentType, parentId, taskSeq, section }).filter(
                ([, value]) => value != null
            )
        );

        return request(
            path(kind, undefined, 'attachments') + '?' + parameters.toString(),
            session()
        );
    },
    list(kind, query, force = false) {
        const parameters = new URLSearchParams(
            Object.entries(query).sort(([a], [b]) => a.localeCompare(b))
        );

        return read(path(kind) + '?' + parameters.toString(), force);
    },
    getChecklist,
    saveChecklist,
    listRecords(kind, query, force = false) {
        const parameters = new URLSearchParams(
            Object.entries(query)
                .filter(([, value]) => value !== '' && value != null)
                .sort(([a], [b]) => a.localeCompare(b))
        );

        return read(path(kind, undefined, 'records') + '?' + parameters.toString(), force);
    },
    getRecord: (kind, id) => read(path(kind, id, 'records')).revalidate,
    saveResults: (kind, id, payload) => mutateRecord(kind, id, 'tasks', 'PATCH', payload),
    closeRecord: (kind, id, payload) => mutateRecord(kind, id, 'close', 'POST', payload),
    reopenRecord: (kind, id, payload) => mutateRecord(kind, id, 'reopen', 'POST', payload),
    clear: () => cache.clear(),
};
